import { useState, useEffect } from "react";
import styles from "./filterModal.module.css";
import TreeView, { flattenTree } from "react-accessible-treeview";
import { FaSquare, FaCheckSquare, FaMinusSquare } from "react-icons/fa";
import { IoMdArrowDropright } from "react-icons/io";
import cx from "classnames";

const isTopLevelSelection = (node, ids, data) => {
    let current = node;
    while (current.parent) {
        const parentId = current.parent;
        if (ids.includes(parentId)) {
            return false;
        }
        const found = data.find(d => d.id === parentId);
        if (!found) break;
        current = found;
    }
    return true;
};

const FilterModal = ({
    authors,
    selectedAuthor,
    setSelectedAuthor,
    usedCategories,
    selectedCategories,
    setSelectedCategories,
    selectedDateFrom,
    setSelectedDateFrom,
    selectedDateTo,
    setSelectedDateTo,
    onClose,
    categories,

}) => {
    const [isOpenAutor, setIsOpenAutor] = useState(false);
    const [isOpenAbteilung, setIsOpenAbteilung] = useState(false);
    const [isOpenDatum, setIsOpenDatum] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const buildCategoryTree = (categories, usedCategories) => {
        const map = new Map();
        const root = { name: "", id: "treeview", children: [] };

        // First pass: initialize all categories
        categories.forEach((cat) => {
            map.set(cat.name, { name: cat.name, id: cat.name, children: [] });
        });

        // Second pass: assign children to parents
        categories.forEach((cat) => {
            const node = map.get(cat.name);
            if (cat.parent_category) {
                const parentNode = map.get(cat.parent_category);
                if (parentNode) {
                    parentNode.children.push(node);
                }
            } else {
                root.children.push(node); // top-level category
            }
        });

        const usedCategoryNames = usedCategories.map(c => c.category_name);
        const prune = (node) => {
            if (node.children.length === 0) {
                // It's a leaf
                return usedCategoryNames.includes(node.name); // Keep only if used
            }

            // Recursively filter children
            node.children = node.children.filter(prune);

            // Keep non-leaf nodes regardless, but drop them if all children got removed
            return node.children.length > 0 || usedCategoryNames.includes(node.name);
        };

        root.children = root.children.filter(prune);
        return root;
    };
    const folder = buildCategoryTree(categories, usedCategories);
    const data = flattenTree(folder);

    const ArrowIcon = ({ isOpen, className }) => {
        const baseClass = "arrow";
        const classes = cx(
            baseClass,
            { [`${baseClass}--closed`]: !isOpen },
            { [`${baseClass}--open`]: isOpen },
            className
        );
        return <IoMdArrowDropright className={classes} />;
    };

    const CheckBoxIcon = ({ variant, ...rest }) => {
        switch (variant) {
            case "all":
                return <FaCheckSquare {...rest} />;
            case "none":
                return <FaSquare {...rest} />;
            case "some":
                return <FaMinusSquare {...rest} />;
            default:
                return null;
        }
    };



    const getAllIds = (node, maxDepth = Infinity, currentDepth = 1) => {
        let ids = [];

        if (currentDepth <= maxDepth) {
            ids.push(node.id);
        }

        if (node.children && node.children.length > 0 && currentDepth < maxDepth) {
            node.children.forEach(child => {
                ids = ids.concat(getAllIds(child, maxDepth, currentDepth + 1));
            });
        }

        return ids;
    };

    const allExpandedIds = getAllIds(folder, 5);

    useEffect(() => {
        setSelectedIds(selectedCategories);
    }, [selectedCategories]);

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.top}>
                    <h2>Filter Optionen</h2>
                    <svg className={styles.close} onClick={onClose} version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="121.31px" height="122.876px" viewBox="0 0 121.31 122.876" enableBackground="new 0 0 121.31 122.876" xmlSpace="preserve"><g><path fillRule="evenodd" clipRule="evenodd" d="M90.914,5.296c6.927-7.034,18.188-7.065,25.154-0.068 c6.961,6.995,6.991,18.369,0.068,25.397L85.743,61.452l30.425,30.855c6.866,6.978,6.773,18.28-0.208,25.247 c-6.983,6.964-18.21,6.946-25.074-0.031L60.669,86.881L30.395,117.58c-6.927,7.034-18.188,7.065-25.154,0.068 c-6.961-6.995-6.992-18.369-0.068-25.397l30.393-30.827L5.142,30.568c-6.867-6.978-6.773-18.28,0.208-25.247 c6.983-6.963,18.21-6.946,25.074,0.031l30.217,30.643L90.914,5.296L90.914,5.296z" /></g></svg>
                </div>

                <div className={styles.modalContent}>
                    {/* Author Filter */}
                    <div id="autor" className={styles.card}>
                        <div className={styles.title}>
                            <p className={styles.name}>Autor:in</p>
                            <svg className={`${styles.gt} ${isOpenAutor ? styles.rotated : ""}`} style={{ cursor: "pointer" }} data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 103.28 122.88" onClick={() => setIsOpenAutor(!isOpenAutor)}>
                                <title>greater-than</title>
                                <polygon points="0 122.88 103.28 76.93 103.28 45.95 0 0 0 33.94 75.46 61.34 0 88.74 0 122.88 0 122.88" />
                            </svg>
                        </div>
                        <div className={`${styles.cardContent} ${isOpenAutor ? styles.show : styles.hide}`}>
                            <select value={selectedAuthor} onChange={(e) => setSelectedAuthor(e.target.value)}>
                                <option value="">Alle</option>
                                {authors.map((author) => (
                                    <option key={author.author_id} value={author.author_id}>{author.author_name}</option>
                                ))}
                            </select>
                        </div>

                    </div>
                    <div id="categories" className={styles.card}>
                        <div className={styles.title}>
                            <p className={styles.name}>Kategorie</p>
                            <svg className={`${styles.gt} ${isOpenAbteilung ? styles.rotated : ""}`} style={{ cursor: "pointer" }} data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 103.28 122.88" onClick={() => setIsOpenAbteilung(!isOpenAbteilung)}>
                                <title>greater-than</title>
                                <polygon points="0 122.88 103.28 76.93 103.28 45.95 0 0 0 33.94 75.46 61.34 0 88.74 0 122.88 0 122.88" />
                            </svg>
                        </div>
                        <div className={`${styles.cardContentCategories} ${isOpenAbteilung ? styles.show : styles.hide}`}>
                            <div className={styles.checkBox}>
                                <TreeView
                                    data={data}
                                    className={styles.tree}
                                    multiSelect
                                    selectedIds={selectedIds}
                                    defaultExpandedIds={allExpandedIds}
                                    propagateSelect
                                    propagateSelectUpwards
                                    togglableSelect

                                    onNodeSelect={(event) => {
                                        const ids = Array.isArray(event.selectedIds)
                                            ? event.selectedIds
                                            : Array.from(event.treeState?.selectedIds ?? []);


                                        // Step 1: Get all selected nodes
                                        let chosenCategories = ids
                                            .map(id => data.find(d => d.id === id))
                                            .filter(Boolean); // remove undefined if any

                                        // Step 2: Filter out nodes that are children of any other selected node


                                        chosenCategories = chosenCategories.filter(node => isTopLevelSelection(node, ids, data));

                                        setSelectedIds(ids);
                                        setSelectedCategories(chosenCategories.map(node => node.id));
                                    }}


                                    nodeRenderer={({
                                        element,
                                        isBranch,
                                        isExpanded,
                                        isSelected,
                                        isHalfSelected,
                                        isDisabled,
                                        getNodeProps,
                                        level,
                                        handleSelect,
                                        handleExpand,
                                    }) => {
                                        return (
                                            <div
                                                {...getNodeProps({ onClick: handleExpand })}
                                                className={styles.treeNodeWrapper}
                                            >
                                                <ArrowIcon
                                                    className={`${styles.arrow} ${!isBranch ? styles.invisible : ""} ${isExpanded ? styles["arrow--open"] : ""}`}
                                                    isOpen={isExpanded}
                                                />
                                                <CheckBoxIcon
                                                    className={styles.checkboxIcon}
                                                    onClick={(e) => {
                                                        handleSelect(e);
                                                        e.stopPropagation();
                                                    }}
                                                    variant={isHalfSelected ? "some" : isSelected ? "all" : "none"}
                                                />
                                                <span
                                                    className={styles.categoryText}
                                                >
                                                    {element.name}
                                                </span>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        </div>

                    </div>
                    <div id="dates" className={styles.card}>
                        <div className={styles.title}>
                            <p className={styles.name}>Datum</p>
                            <svg className={`${styles.gt} ${isOpenDatum ? styles.rotated : ""}`} style={{ cursor: "pointer" }} data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 103.28 122.88" onClick={() => setIsOpenDatum(!isOpenDatum)}>
                                <title>greater-than</title>
                                <polygon points="0 122.88 103.28 76.93 103.28 45.95 0 0 0 33.94 75.46 61.34 0 88.74 0 122.88 0 122.88" />
                            </svg>
                        </div>
                        <div className={`${styles.cardContent} ${isOpenDatum ? styles.show : styles.hide}`}>
                            <div className={styles.dateDiv}>
                                <div className={styles.dateDiv2}>
                                    <div>
                                        <label>Datum von:</label>
                                    </div>
                                    <div>
                                        <input
                                            type="date"
                                            value={selectedDateFrom}
                                            onChange={(e) => setSelectedDateFrom(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Date To */}
                                <div className={styles.dateDiv2}>
                                    <div>
                                        <label>Datum bis:</label>
                                    </div>
                                    <div>
                                        <input
                                            type="date"
                                            value={selectedDateTo}
                                            onChange={(e) => setSelectedDateTo(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterModal;
